import { AUTH_CODE, EMAIL_DOMAIN, isEmailLocalPart, normalizeAuthCode } from '@tomato/shared';
import { useEffect, useRef, useState } from 'react';

import { api, ApiError } from '../api/client';
import { Button, CenterCard, Label, Logo, Notice, TextField } from '../components/ui';
import { isInAppBrowser, isKakaoInApp, isMobile, openInExternalBrowser } from '../lib/browserEnv';
import {
  cancelPasskeyCeremony,
  conditionalMediationAvailable,
  isUserCancel,
  loginWithPasskey,
  passkeySupported,
  startConditionalPasskeyLogin,
} from '../lib/passkey';
import { fetchInviterNickname, getStoredRef } from '../lib/referral';
import { useSessionStore } from '../store/session';

/** 재발송 대기. 서버 레이트리밋(이메일 분당 1회)과 맞춘다. */
const RESEND_COOLDOWN_SEC = 60;

interface MeResponse {
  user: import('@tomato/shared').MeUser;
  onboarded: boolean;
}

export function LoginScreen() {
  const setSession = useSessionStore((s) => s.setSession);

  const [step, setStep] = useState<'email' | 'code'>('email');
  // 아이디(로컬파트)만 입력받고 도메인은 붙여서 보낸다.
  const [localPart, setLocalPart] = useState('');
  const [code, setCode] = useState('');
  const email = `${localPart.trim()}@${EMAIL_DOMAIN}`;
  const localValid = isEmailLocalPart(localPart.trim());
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [cooldown, setCooldown] = useState(0);

  const [inApp] = useState(isInAppBrowser);
  // 패스키 '로그인' 버튼은 이미 등록한 재방문자용 지름길이다.
  // 모바일·인앱에서는 패스키가 없는 신규 방문자에게 QR(교차기기)·인앱 실패라는 막다른 길이 되므로 감춘다.
  // (등록은 로그인 후 설정/환영 화면에서 하고, 데스크톱에서는 QR 교차기기가 정상 동작해 유지한다.)
  const [showPasskeyLogin] = useState(() => passkeySupported() && !isInAppBrowser() && !isMobile());
  // 초대 링크(?ref=)로 왔으면 초대자 활동명을 물어와 "○○님이 초대했어요"를 띄운다.
  const [inviter, setInviter] = useState<string | null>(null);

  const codeInput = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const ref = getStoredRef();
    if (!ref) return;
    let cancelled = false;
    void fetchInviterNickname(ref).then((nickname) => {
      if (!cancelled) setInviter(nickname);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (cooldown <= 0) return;
    const t = setTimeout(() => setCooldown((n) => n - 1), 1000);
    return () => clearTimeout(t);
  }, [cooldown]);

  useEffect(() => {
    if (step === 'code') codeInput.current?.focus();
  }, [step]);

  /**
   * 자동완성 패스키 로그인(Conditional UI). 아이디 입력 단계에서 백그라운드로 대기하다가,
   * 사용자가 아이디칸 자동완성에서 등록된 패스키를 고르면 바로 로그인된다.
   * 패스키가 없으면(신규) 아무 일도 없음 — 인앱은 WebAuthn이 막혀 시도하지 않는다.
   */
  useEffect(() => {
    if (step !== 'email' || inApp) return;
    let cancelled = false;
    void (async () => {
      if (!(await conditionalMediationAvailable())) return;
      const result = await startConditionalPasskeyLogin();
      if (cancelled || !result) return;
      setSession(await api.get<MeResponse>('/api/me'));
    })();
    return () => {
      cancelled = true;
      cancelPasskeyCeremony();
    };
  }, [step, inApp, setSession]);

  async function signInWithPasskey() {
    if (busy) return;
    setBusy(true);
    setError('');
    try {
      await loginWithPasskey();
      // 세션이 생겼으니 본인 정보를 받아 화면을 전환한다.
      setSession(await api.get<MeResponse>('/api/me'));
    } catch (err) {
      if (isUserCancel(err)) return; // 사용자가 취소 — 조용히
      // 카톡 등 인앱 브라우저는 패스키가 막혀 있는 경우가 많다 — 원인을 짚어준다.
      setError(
        inApp
          ? '카톡 안에서는 패스키가 잘 안 돼요. 크롬·사파리로 열거나 인증번호로 로그인해 주세요.'
          : err instanceof ApiError
            ? '이 기기에 등록된 패스키가 없어요. 인증번호로 로그인해 주세요.'
            : '패스키 로그인에 실패했어요. 인증번호로 로그인해 주세요.',
      );
    } finally {
      setBusy(false);
    }
  }

  async function requestCode() {
    if (busy || !localValid) return;
    setBusy(true);
    setError('');
    try {
      await api.post('/auth/request', { email });
      setStep('code');
      setCode('');
      setCooldown(RESEND_COOLDOWN_SEC);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : '잠시 후 다시 시도해 주세요.');
    } finally {
      setBusy(false);
    }
  }

  async function verify(value: string) {
    if (busy || value.length !== AUTH_CODE.length) return;
    setBusy(true);
    setError('');
    try {
      await api.post('/auth/verify', { email, code: value });
      // 세션 쿠키가 생겼으니 본인 정보를 받아 화면을 전환한다.
      setSession(await api.get<MeResponse>('/api/me'));
    } catch (err) {
      setError(err instanceof ApiError ? err.message : '잠시 후 다시 시도해 주세요.');
      setCode('');
      codeInput.current?.focus();
    } finally {
      setBusy(false);
    }
  }

  function onCodeChange(raw: string) {
    const next = normalizeAuthCode(raw);
    setCode(next);
    // 6자리가 채워지면 바로 확인한다(붙여넣기 포함).
    if (next.length === AUTH_CODE.length) void verify(next);
  }

  if (step === 'email') {
    return (
      <CenterCard>
        <Logo sub="사내 벼룩시장" />
        {inviter && (
          <p className="mb-4 rounded-xl bg-tomato-50 px-3.5 py-2.5 text-center text-[13px] leading-relaxed text-tomato-700">
            🍅 <b className="font-semibold">{inviter}</b>님이 토마토 마켓에 초대했어요
          </p>
        )}
        <Label>회사 메일 아이디</Label>
        {/* 아이디만 받고 @hecto.co.kr는 필드 안에 고정으로 보여준다. */}
        <div className="flex items-stretch overflow-hidden rounded-xl bg-ink-50 text-[15px] hairline transition focus-within:bg-surface focus-within:shadow-[inset_0_0_0_1.5px_var(--color-tomato-500)]">
          <input
            autoComplete="username webauthn"
            inputMode="email"
            placeholder="아이디"
            value={localPart}
            disabled={busy}
            autoFocus
            // 붙여넣기로 전체 이메일이 들어오면 로컬파트만 남긴다.
            onChange={(e) => setLocalPart(e.target.value.replace(/@.*$/, '').trim())}
            onKeyDown={(e) => e.key === 'Enter' && void requestCode()}
            className="min-w-0 flex-1 bg-transparent px-3.5 py-3 text-ink-900 outline-none placeholder:text-ink-300 disabled:text-ink-400"
          />
          <span className="flex select-none items-center pr-3.5 pl-1 text-ink-400">
            @{EMAIL_DOMAIN}
          </span>
        </div>
        <Button
          className="mt-4 w-full"
          disabled={busy || !localValid}
          onClick={() => void requestCode()}
        >
          {busy ? '보내는 중…' : '인증번호 받기'}
        </Button>

        {showPasskeyLogin && (
          <>
            <div className="my-4 flex items-center gap-3 text-[11px] text-ink-300">
              <span className="h-px flex-1 bg-ink-100" />
              또는
              <span className="h-px flex-1 bg-ink-100" />
            </div>
            <Button
              variant="secondary"
              className="w-full"
              disabled={busy}
              onClick={() => void signInWithPasskey()}
            >
              🔑 패스키로 로그인
            </Button>
          </>
        )}

        <Notice>{error}</Notice>
        <p className="mt-5 text-center text-xs leading-relaxed text-slate-400">
          회사 메일 주소가 곧 입장권이에요.
          <br />
          입력한 메일로 인증번호를 보내드려요.
          <br />
          처음이시면 인증 후 바로 가입 화면으로 넘어갑니다.
        </p>

        {/* 카톡 등 인앱 브라우저 안내 — 패스키가 여기선 잘 안 된다. */}
        {inApp && (
          <div className="mt-4 rounded-xl bg-amber-50 px-3.5 py-2.5 text-[12px] leading-relaxed text-amber-700">
            <p>
              지금 {isKakaoInApp() ? '카톡' : '앱'} 안 브라우저예요. 패스키·홈 화면 추가는{' '}
              <b>크롬·사파리</b>에서 잘 돼요.{' '}
              {isKakaoInApp() ? (
                '아래 버튼을 누르거나 '
              ) : (
                <>오른쪽 위(갤럭시는 오른쪽 아래) 메뉴(⋮)에서 </>
              )}
              <b>{isKakaoInApp() ? '“다른 브라우저로 열기”' : '“기본 브라우저로 열기”'}</b>
              {isKakaoInApp() ? '로 열어주세요.' : '를 눌러주세요.'}
            </p>
            {isKakaoInApp() && (
              <button
                type="button"
                onClick={openInExternalBrowser}
                className="mt-2.5 inline-flex items-center rounded-lg bg-amber-600 px-3 py-1.5 text-[12px] font-medium text-white transition-colors hover:bg-amber-700"
              >
                기본 브라우저로 열기
              </button>
            )}
          </div>
        )}
      </CenterCard>
    );
  }

  return (
    <CenterCard>
      <Logo sub={`${email} 로 보냈어요`} />
      <Label hint={`${AUTH_CODE.length}자리 숫자`}>인증 코드</Label>
      <TextField
        ref={codeInput}
        inputMode="numeric"
        autoComplete="one-time-code"
        placeholder="000000"
        maxLength={AUTH_CODE.length}
        value={code}
        disabled={busy}
        onChange={(e) => onCodeChange(e.target.value)}
        onKeyDown={(e) => e.key === 'Enter' && void verify(code)}
        className="text-center text-2xl font-semibold tracking-[0.4em]"
      />
      <Button
        className="mt-4 w-full"
        disabled={busy || code.length !== AUTH_CODE.length}
        onClick={() => void verify(code)}
      >
        {busy ? '확인 중…' : '로그인'}
      </Button>
      <Notice>{error}</Notice>

      <div className="mt-4 flex items-center justify-between text-xs">
        <Button
          variant="ghost"
          className="px-0 py-0"
          onClick={() => {
            setStep('email');
            setError('');
          }}
        >
          ← 주소 다시 입력
        </Button>
        <Button
          variant="ghost"
          className="px-0 py-0"
          disabled={busy || cooldown > 0}
          onClick={() => void requestCode()}
        >
          {cooldown > 0 ? `재발송 ${cooldown}초` : '코드 재발송'}
        </Button>
      </div>
      <p className="mt-5 text-center text-xs leading-relaxed text-slate-400">
        메일이 안 보이면 스팸함도 확인해 주세요.
        <br />
        코드는 10분 뒤 만료됩니다.
      </p>
    </CenterCard>
  );
}
